import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import { Button, Grid, TextField, Select, MenuItem, FormControl, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NumberField from 'components/library/NumberField';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ShopsHelper from 'helpers/ShopsHelper';
import { ShopContext } from 'Context/ShopContext';
import ShopSettingsService from 'services/ShopSettingsService';
import { roundOff } from 'helpers/NumberFormatHelper';
import { CircularProgress } from '@mui/material';

function AddPaymentPopup(props) {
  const { t: tCommon } = useTranslation('common');
  const { togglePaymentTab, addPaymentToCustomer, currentBalance } = props;
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [paymentTime, setPaymentTime] = useState(new Date());
  const [isClicked, setIsClicked] = useState(false);
  const [finalBalance, setFinalBalance] = useState('');
  const { shop } = useContext(ShopContext);
  const [accountHeads, setAccountHeads] = useState([]);
  const [selectedAccountHead, setSelectedAccountHead] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAccountHeads = async () => {
      try {
        const heads = await ShopSettingsService.getPaymentModes();
        setAccountHeads(heads || []);
        if (heads && heads.length > 0) {
          setSelectedAccountHead(heads[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch payment modes:', err);
      }
    };
    fetchAccountHeads();
  }, []);

  const addPayment = () => {
    setIsClicked(true);
    setSaving(true);
    const selectedHead = accountHeads.find((ah) => ah.id === selectedAccountHead);
    let data = {
      note,
      payment_time: new Date(paymentTime).toISOString(),
      payment_mode_id: selectedAccountHead || null,
      payment_modes: selectedAccountHead
        ? [
            {
              account_head_id: selectedAccountHead,
              mode_id: selectedAccountHead,
              name: selectedHead?.name || selectedHead?.mode || 'Cash',
              amount: Math.abs(amount),
            },
          ]
        : [],
    };
    if (amount < 0) data.debit = Math.abs(amount);
    else data.credit = amount * 1;
    try {
      addPaymentToCustomer(data);
    } catch (error) {
      setSaving(false);
      setIsClicked(false);
    }
  };

  const calculateByAmount = (amt) => {
    setAmount(amt);
    if (amt === '' || amt === '-') {
      setFinalBalance(currentBalance);
    } else {
      let balance = roundOff(currentBalance + Number(amt));
      setFinalBalance(balance);
    }
  };

  const calculateByFinalBalance = (balance) => {
    setFinalBalance(balance);
    if (balance === '' || balance === '-') {
      setAmount('');
    } else {
      let amt = roundOff(Number(balance) - currentBalance);
      setAmount(amt);
    }
  };

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tCommon('Add Payment')}</h2>
          <CloseIcon
            onClick={() => {
              if (!saving) {
                togglePaymentTab('');
              }
            }}
            style={{
              opacity: saving ? 0.5 : 1,
            }}
          />
        </div>
        <div className={styles.inputSec}>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.labelInput}>{tCommon('Balance')}</h3>
            </Grid>
            <Grid item xs={8} sm={8} spacing={2} className={styles.inputLabelContainer}>
              <h3 className={styles.labelInput}>{currentBalance}</h3>
            </Grid>
          </Grid>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.labelInput}>{tCommon('Amount')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <NumberField
                value={
                  amount === '' || amount === '-' || amount === undefined
                    ? amount || ''
                    : ShopsHelper.getAmountFormatted(shop, amount)
                }
                onChange={(e) => calculateByAmount(e.target.value)}
              />
            </Grid>
          </Grid>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.labelInput}>{tCommon('Payment Mode')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={selectedAccountHead}
                  onChange={(e) => setSelectedAccountHead(e.target.value)}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                      style: {
                        maxHeight: 250,
                      },
                    },
                  }}
                >
                  {accountHeads.map((ah) => (
                    <MenuItem key={ah.id} value={ah.id}>
                      {ah.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.labelInput}>{tCommon('Payment Date')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  ampm
                  format="dd/MM/yyyy hh:mm a"
                  value={paymentTime}
                  onChange={(paymentTime) => {
                    setPaymentTime(paymentTime);
                  }}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      fullWidth: true,
                      size: 'small',
                      margin: 'none',
                      id: 'date-payment',
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.labelInput}>{tCommon('Note')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField
                label={tCommon('Description')}
                variant="outlined"
                size="small"
                className={styles.numberInput}
                name="description"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.labelInput}>{tCommon('Final Balance')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <NumberField
                className={styles.numberInput}
                value={
                  finalBalance === '' || finalBalance === '-' || finalBalance === undefined
                    ? finalBalance || ''
                    : ShopsHelper.getAmountFormatted(shop, finalBalance)
                }
                onChange={(e) => calculateByFinalBalance(e.target.value)}
              />
            </Grid>
          </Grid>
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={togglePaymentTab}
              className={styles.closeBtn}
              disabled={saving}
            >
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              style={{ backgroundColor: '#00a65a' }}
              disabled={saving || isClicked || amount === '' || amount === '-' || note === ''}
              onClick={addPayment}
            >
              {saving ? (
                <>
                  <CircularProgress size={18} color="inherit" style={{ marginRight: 8 }} />
                </>
              ) : (
                tCommon('Add Payment')
              )}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}

export default AddPaymentPopup;
